import { Controller } from '@hotwired/stimulus';

/**
 * Subcontract Agreed Amount Controller
 *
 * Rend VIFS, pendant la frappe, les deux avertissements de la modale
 * « Ajouter / Modifier le sous-traitant » (`app/views/subcontracts/_modal.html.erb`) :
 *
 *   1. « le cumul payé en direct dépasse le plafond agréé saisi » (target `paidMessage`)
 *   2. « le plafond agréé saisi dépasse le total du marché »      (target `overMessage`)
 *
 * Le serveur rend DÉJÀ l'encadré et les deux phrases à l'ouverture ; ce contrôleur ne
 * fait que révéler / masquer des nœuds existants et y réécrire du `textContent`.
 *
 * CE CONTRÔLEUR N'EST PAS UNE GARDE (décision épinglée D-1 : ces deux messages sont
 * NON BLOQUANTS, « Enregistrer » reste actif). Le seuil dur `AMOUNT_CEILING` et la
 * validation de présence vivent côté serveur et ne sont jamais dupliqués ici.
 *
 * AUCUNE MICROCOPIE RENDUE, AUCUNE CLASSE, AUCUNE COULEUR, AUCUNE ICÔNE dans ce
 * fichier. (Les commentaires, eux, sont en français : ce qui est proscrit, c'est
 * toute chaîne qui ATTEINT l'écran depuis ici.) Raison : `ui-lint-erb.sh` sort sur tout ce qui n'est pas `.erb` et
 * `scripts/lint-terminology.sh` ne lit pas non plus les `.js`, donc une microcopie
 * ou un token de design figé ici échapperait à TOUTE relecture automatique. Les deux
 * phrases descendent en gabarit annoté (`%<agreed>s`, `%<total>s`, `%<paid>s`) via
 * `data-template`, posé par `SubcontractsHelper` ; on n'y substitue que des montants.
 * Seule exception assumée : la classe utilitaire `hidden`, déjà posée par le serveur,
 * qu'on se contente de basculer.
 *
 * `hidden` est basculé en CLASSE et JAMAIS en attribut booléen HTML : la règle
 * `[hidden] { display: none }` de la feuille de style UA survivrait au retrait de la
 * classe Tailwind et le nœud ne réapparaîtrait plus jamais, sans la moindre erreur.
 *
 * Targets:
 * - input:        le champ « Plafond sous-traité agréé (HT) » (`<input type="number">`).
 *                 Porte AUSSI les deux constantes de page en `dataset` :
 *                 `data-contract-total-cents` et `data-paid-cents`.
 * - box:          l'encadré d'avertissement (rendu inconditionnellement, `hidden` au repos).
 * - paidMessage:  le span « cumul payé > plafond agréé », porte son `data-template`.
 * - overMessage:  le span « plafond agréé > total du marché », porte son `data-template`.
 *
 * Values: AUCUNE — délibérément. Les deux constantes de page sont lues en `dataset.*`
 * sur le CHAMP et non en Stimulus *values*, parce qu'une *value* doit être posée sur
 * l'élément porteur du `data-controller` (ici le `<form>`) : un
 * `data-…-contract-total-cents-value` posé sur le champ rendrait `undefined` EN SILENCE.
 *
 * Actions:
 * - validate: recalcule les deux avertissements. Câblé sur `input->…#validate` (frappe),
 *   appelé au `connect()`. PAS d'écouteur `modal:close` — voir plus bas pourquoi.
 *
 * @example
 * <form data-controller="subcontract-agreed-amount">
 *   <input type="number"
 *          data-subcontract-agreed-amount-target="input"
 *          data-action="input->subcontract-agreed-amount#validate"
 *          data-contract-total-cents="12000000"
 *          data-paid-cents="0">
 *   <div class="hidden" data-subcontract-agreed-amount-target="box"
 *        aria-live="polite" aria-atomic="false">
 *     <span class="hidden"
 *           data-subcontract-agreed-amount-target="paidMessage"
 *           data-template="… (%<paid>s) … (%<agreed>s)."></span>
 *     <span class="hidden"
 *           data-subcontract-agreed-amount-target="overMessage"
 *           data-template="… (%<agreed>s) … (%<total>s). …"></span>
 *   </div>
 * </form>
 */
export default class extends Controller {
  static targets = ['input', 'box', 'overMessage', 'paidMessage'];

  connect() {
    // Le tuple d'état est remis à zéro à chaque connexion : une restauration de
    // cache Turbo Drive reconstruit une INSTANCE neuve sur un DOM qui porte encore
    // l'état précédent, donc le premier `validate()` doit toujours écrire.
    this.lastState = null;

    // Le serveur rend l'état PERSISTÉ ; le champ, lui, peut déjà porter autre chose
    // après une restauration de cache Turbo Drive (qui restaure classes et
    // `textContent` sans jamais émettre d'`input`). Une seule source à l'écran.
    this.validate();
  }

  // PAS d'écouteur `modal:close`, et c'est délibéré. Une version antérieure de ce
  // fichier en posait un pour « rafraîchir à la fermeture » : il ne pouvait RIEN
  // faire. `modal_controller.js#close` ne touche AUCUNE valeur de champ, donc à la
  // fermeture la saisie est inchangée, donc le tuple d'état est inchangé, donc la
  // garde ci-dessous sortait à chaque fois. Du code qui a l'air utile et ne l'est
  // pas coûte plus cher que pas de code du tout.
  //
  // Conséquence assumée (arbitrage Charles, 2026-08-04) : « Annuler » ne
  // réinitialise pas le formulaire — défaut PRÉEXISTANT à ce lot — donc à la
  // réouverture le champ affiche encore la saisie abandonnée, et l'avertissement
  // la décrit fidèlement. SI quelqu'un fait un jour réinitialiser les champs à la
  // fermeture, il devra RAJOUTER une revalidation ici, sans quoi l'encadré
  // survivrait à la valeur qui l'a déclenché.

  validate() {
    if (!this.hasInputTarget || !this.hasBoxTarget) return;

    const input = this.inputTarget;

    // SAISIE REFUSÉE PAR LE NAVIGATEUR : `value === ''` et `valueAsNumber === NaN`,
    // ALORS QUE L'UTILISATEUR VOIT DES CARACTÈRES À L'ÉCRAN. La traiter comme
    // « champ vide » masquerait l'avertissement sur une valeur visible — le symptôme
    // d'origine, réintroduit par une autre porte. On ne touche donc à RIEN : ni au
    // DOM, ni au tuple d'état, et l'encadré reste sur son dernier état connu.
    //
    // ⚠️ MESURÉ dans Chromium sur ce champ, contre l'intuition : ni le COLLAGE de
    // « 150 000 » (l'espace est supprimé, la valeur devient 150000) ni la VIRGULE
    // décimale française (« 150000,50 » → 150000.5) ne passent par ici — le
    // navigateur les normalise. Ce chemin n'est atteint que par une saisie
    // réellement non numérique (« e », « - » isolé). Le revers assumé : l'encadré
    // peut alors afficher un montant PÉRIMÉ jusqu'au retour à une valeur valide.
    // Le contre-choix — tout masquer — a été écarté : il rend l'écran muet sur une
    // saisie visible, ce qui est le défaut que ce lot corrige.
    if (input.validity.badInput) return;

    // Les deux constantes de page. Chaque axe est indépendant : un attribut absent
    // ou illisible éteint SON avertissement et laisse l'autre fonctionner, plutôt
    // que de figer les deux.
    const totalCents = parseInt(input.dataset.contractTotalCents, 10);
    const paidCents = parseInt(input.dataset.paidCents, 10);

    // `valueAsNumber` et jamais `.value` : sur un `<input type="number">` la
    // propriété `value` rend `''` pour tout contenu non numérique. (Corollaire : la
    // normalisation « retrait des blancs + virgule → point » du contrôleur voisin
    // `subcontract_cap_controller.js` est DU CODE MORT sur ce type de champ — ces
    // caractères n'atteignent jamais le JS. Ne pas la recopier.)
    const enteredAmount = input.valueAsNumber;

    // Centimes ENTIERS des deux côtés : on compare des entiers, jamais des
    // flottants, pour qu'un cumul à l'euro près ne bascule pas sur une erreur
    // d'arrondi binaire.
    const agreedCents = Number.isNaN(enteredAmount) ? null : Math.round(enteredAmount * 100);

    // Champ vide (S1) ou plafond ≤ 0 (S2) ⇒ aucun avertissement : c'est une erreur
    // de VALIDATION, qui a son propre canal serveur, pas un avertissement.
    const hasAgreed = agreedCents !== null && agreedCents > 0;

    // Comparateurs STRICTS des deux côtés, alignés sur le serveur
    // (`Subcontract#agreed_amount_exceeds_contract_total?`, `subcontract.rb:108`, et
    // `SubcontractsHelper#subcontract_paid_exceeds_agreed?`). Un `>=` crierait en
    // permanence sur un plafond exactement consommé — l'état banal de fin de marché —
    // et le message disparaîtrait au premier rechargement, le serveur ne le rendant pas.
    const overActive = hasAgreed && !Number.isNaN(totalCents) && agreedCents > totalCents;
    const paidActive = hasAgreed && !Number.isNaN(paidCents) && paidCents > agreedCents;

    // GARDE D'ÉTAT. L'encadré est une live-region (`aria-live="polite"`) : réécrire
    // son contenu à chaque frappe le ferait annoncer caractère par caractère. On ne
    // touche au DOM que si l'état change vraiment.
    //
    // Le tuple compare des NOMBRES et des BOOLÉENS, JAMAIS les chaînes rendues : le
    // serveur formate ses montants avec des espaces ASCII (U+0020, `fr.yml`) et
    // `Intl.NumberFormat('fr-FR')` avec U+202F / U+00A0. Les deux chaînes diffèrent
    // donc TOUJOURS — d'un codepoint sous 1 000 €, de deux au-delà, de trois
    // au-delà du million (mesuré) : une garde par chaîne ne court-circuiterait
    // jamais, quel que soit le montant.
    if (
      this.lastState !== null &&
      this.lastState.agreedCents === agreedCents &&
      this.lastState.overActive === overActive &&
      this.lastState.paidActive === paidActive
    ) {
      return;
    }
    this.lastState = { agreedCents, overActive, paidActive };

    // Ordre de rendu aligné sur la vue : le cumul payé d'abord (de l'argent DÉJÀ
    // sorti, donc plus actionnable), le dépassement du marché ensuite.
    if (this.hasPaidMessageTarget) {
      this.#renderMessage(this.paidMessageTarget, paidActive, () => ({
        '%<paid>s': this.#format(paidCents),
        '%<agreed>s': this.#format(agreedCents),
      }));
    }

    if (this.hasOverMessageTarget) {
      this.#renderMessage(this.overMessageTarget, overActive, () => ({
        '%<agreed>s': this.#format(agreedCents),
        '%<total>s': this.#format(totalCents),
      }));
    }

    this.boxTarget.classList.toggle('hidden', !(paidActive || overActive));
  }

  // `buildTokens` est une FONCTION et pas un objet : sur un axe inactif les montants
  // n'ont pas de valeur exploitable (`agreedCents` peut être `null`) et il n'y a
  // aucune raison de les formater pour les jeter.
  #renderMessage(target, active, buildTokens) {
    target.classList.toggle('hidden', !active);
    target.textContent = active ? this.#fill(target.dataset.template, buildTokens()) : '';
  }

  // Substitution des jetons annotés du gabarit serveur.
  //
  // `replaceAll` et jamais `replace`, qui ne remplacerait que la PREMIÈRE occurrence :
  // `%<agreed>s` apparaît dans les DEUX gabarits et rien n'interdit qu'il apparaisse
  // deux fois dans l'un d'eux demain.
  //
  // Remplacement en FORME FONCTION `() => value` et jamais en chaîne : passée en
  // chaîne, `$&` / `$'` / `$1` seraient interprétés comme des références de motif.
  // Un montant n'en contient pas aujourd'hui, mais la microcopie appartient au
  // serveur et ce fichier n'a rien à supposer de son contenu.
  #fill(template, tokens) {
    if (!template) return '';

    return Object.entries(tokens).reduce(
      (text, [token, value]) => text.replaceAll(token, () => value),
      template
    );
  }

  #format(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }
}
